import React from 'react';

import Cookie from 'js-cookie';
import { req, useApiGet, useApiLoading } from 'react-reqq-lite';
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import { IoClose } from 'react-icons/io5';
import { useHistory } from 'react-router-dom';

import { showModal } from 'modules/common/modal';
import store from 'setup/store';
import { getAdminProfile } from 'modules/admin/profile/actions';
import { getBranchProfile } from 'modules/branch/profile/actions';
import { GET_ADMIN_PROFILE } from 'modules/admin/profile/constant';
import { GET_BRANCH_PROFILE } from 'modules/branch/profile/constant';
import { getLocationProfile } from 'modules/location/profile/actions';
import { getMerchantProfile } from 'modules/merchant/profile/actions';
import { GET_MERCHANT_PROFILE } from 'modules/merchant/profile/constant';
import { GET_LOCATION_PROFILE } from 'modules/location/profile/constant';
import { getPartnerProfile } from 'modules/partner/profile/actions';
import { GET_PARTNER_PROFILE } from 'modules/partner/profile/constant';
// import { Button } from 'modules/common/input';

import * as c from './constants';
import * as actions from './actions';

export const useAuth = () => {
  const auth = useApiGet(c.AUTHENTICATION, {});
  React.useEffect(() => {
    const token = Cookie.get('_token');
    const type = Cookie.get('_userType');
    req.set(c.AUTHENTICATION, {
      isAuthenticated: !!token,
      type,
    });
  }, []);
  return [
    typeof auth.isAuthenticated === 'boolean',
    auth.isAuthenticated,
    auth.type,
  ];
};

export const withAclReq = (requestByType, defaultRequest) => {
  const state = store.getState().reqq;
  const type = state[c.AUTHENTICATION]?.type;
  return {
    ...defaultRequest,
    ...requestByType(state)[type],
  };
};

export const useLogin = (authType) => {
  const isApiLoading = useApiLoading(c.LOGIN, 'post');
  const login = React.useCallback(
    (payload, callback, ecb) => {
      const loginActionByType = {
        ADMIN: () => actions.adminLogin(payload, callback, ecb),
        VENDOR: () => actions.vendorLogin(payload, callback, ecb),
        LOCATION: () => actions.locationLogin(payload, callback, ecb),
        PARTNER: () => actions.partnerLogin(payload, callback, ecb),
      };
      if (authType) {
        loginActionByType[authType]();
      }
    },
    [authType]
  );

  const canProceed = ({ time_remaining_for_retry } = {}) => {
    showModal({
      content: (onClose) => (
        <div className="divide-y">
          <div className="p-5 bg-gray-50 font-semibold flex gap-3 rounded-lg">
            <div className="flex-1">Account Temporarily Locked</div>
            <IoClose className="h-5 w-5 cursor-pointer" onClick={onClose} />
          </div>
          <div className="p-5 text-sm text-gray-600">
            <div>
              You are temporarily locked for{' '}
              <span className="font-bold">
                {dayjs(time_remaining_for_retry).diff(dayjs(), 'seconds') <= 59
                  ? `${dayjs(time_remaining_for_retry).diff(
                      dayjs(),
                      'seconds'
                    )} seconds`
                  : `${dayjs(time_remaining_for_retry).diff(
                      dayjs(),
                      'minutes'
                    )} minute(s)`}
              </span>{' '}
              due to multiple invalid login attempts.
            </div>
          </div>
        </div>
      ),
      focusTrap: false,
      modalSize: 'modal-3xs',
      noClose: true,
      containerClassName: '!p-0',
    });
  };

  return [isApiLoading, login, canProceed];
};

export const useSignUp = (authType) => {
  const isApiLoading = useApiLoading(c.SIGN_UP, 'post');
  const signUp = React.useCallback(
    (payload, callback) => {
      const actionByType = {
        VENDOR: () => actions.vendorSignUp(payload, callback),
      };
      if (authType) {
        actionByType[authType]();
      }
    },
    [authType]
  );
  return [isApiLoading, signUp];
};

export const useGetMyProfile = (authType) => {
  const getProfileConst = {
    ADMIN: GET_ADMIN_PROFILE,
    MERCHANT: GET_MERCHANT_PROFILE,
    BRANCH: GET_BRANCH_PROFILE,
    LOCATION: GET_LOCATION_PROFILE,
    PARTNER: GET_PARTNER_PROFILE,
  };
  const isApiLoding = useApiLoading(getProfileConst[authType], 'get');
  const profile = useApiGet(getProfileConst[authType], {});
  const history = useHistory();

  React.useEffect(() => {
    const profileActionByType = {
      ADMIN: getAdminProfile,
      MERCHANT: getMerchantProfile,
      BRANCH: getBranchProfile,
      LOCATION: getLocationProfile,
      PARTNER: getPartnerProfile,
    };

    if (authType) {
      profileActionByType[authType](
        null,
        () => {},
        (err) => {
          if (err?.response?.error === 'token_session_mismatch') {
            history.push('/logout');
          }
        }
      );
    }
  }, [authType, history]);

  React.useEffect(() => {
    req.set('AUTH/profile', profile);
  }, [profile]);

  return [isApiLoding, profile];
};

export const useCurrentProfile = () => {
  const profile = React.useContext(c.AUTH_PROFILE);
  const state = useApiGet('AUTH/profile', profile);
  return [state];
};

export const usePostForgotPassword = () => {
  const isApiLoading = useApiLoading(c.FORGOT_PASSWORD, 'post');
  const action = (payload, cb) => actions.postForgotPassword(payload, cb);

  return [isApiLoading, action];
};

export const usePasswordExpired = () => {
  const isApiLoading = useApiLoading(c.PASSWORD_EXPIRED, 'post');
  const action = (payload, cb) => actions.passwordExpired(payload, cb);

  return [isApiLoading, action];
};

export const usePostResetPassword = () => {
  const isApiLoading = useApiLoading(c.RESET_PASSWORD, 'post');
  const action = (payload, token, cb, ecb) =>
    actions.postResetPassword(payload, token, cb, ecb);

  return [isApiLoading, action];
};

export const useGetTokenExpiry = (token, cb, ecb) => {
  const isApiLoading = useApiLoading(c.TOKEN_EXPIRY, 'get');
  const data = useApiGet(c.TOKEN_EXPIRY, {});

  React.useEffect(() => {
    actions.getTokenExpiry(token, cb, ecb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return [isApiLoading, data];
};

export const useUpdatePassword = () => {
  const isApiLoading = useApiLoading(c.UPDATE_PASSWORD, 'post');
  const update = React.useCallback((payload, cb) => {
    actions.updatePassword(payload, cb);
  }, []);
  return [isApiLoading, update];
};

export const useSelectAuthMethod = () => {
  const isApiLoading = useApiLoading(c.SELECT_AUTH_METHOD, 'post');
  const post = React.useCallback((params, cb) => {
    actions.selectAuthMethod(params, cb);
  }, []);
  return [isApiLoading, post];
};

export const useAuthOtp = () => {
  const isApiLoading = useApiLoading(c.AUTH_OTP, 'post');
  const post = React.useCallback((params, cb) => {
    actions.authOtp(params, cb);
  }, []);
  return [isApiLoading, post];
};

export const useCheckToken = (callback = () => {}) => {
  React.useMemo(() => {
    let lastToken = Cookie.get('_token');
    const check = debounce(() => {
      const newToken = Cookie.get('_token');
      if (lastToken !== newToken) {
        try {
          callback(lastToken, newToken);
        } finally {
          lastToken = newToken;
        }
      }
    });

    window.addEventListener('focus', check);
    return () => {
      window.removeEventListener('focus', check);
    };
  }, [callback]);
};

export const useGetNotification = (params, cb) => {
  const isApiLoading = useApiLoading(c.GET_NOTIFICATION, 'get');
  const isAppendLoading = useApiLoading(c.GET_NOTIFICATION_APPEND, 'get');
  const data = useApiGet(`${c.GET_NOTIFICATION}.data`, []);
  const pagination = useApiGet(`${c.GET_NOTIFICATION}.pagination`, {});

  const refresh = React.useCallback(
    ($params, $cb) => {
      let args = {
        ...params,
        ...$params,
      };

      if (typeof $params === 'function') args = $params(params);

      actions.getNotification(args, $cb);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(params)]
  );

  const append = ($params) => actions.getAppendNotification($params);
  const pushNotification = React.useCallback(($data, $cb) => {
    req.set(c.GET_NOTIFICATION, (state) => ({
      ...(state[c.GET_NOTIFICATION] || {}),
      data: [$data]?.concat(state[c.GET_NOTIFICATION]?.data || []),
    }));

    req.set(c.GET_NEW_NOTIFICATION, (state) => ({
      ...(state[c.GET_NEW_NOTIFICATION] || {}),
      total_new: state[c.GET_NEW_NOTIFICATION]?.total_new + 1,
    }));
    if ($cb) $cb?.();
  }, []);

  React.useEffect(() => {
    refresh(params, cb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params), refresh]);

  return [
    isApiLoading || isAppendLoading,
    data,
    pagination,
    refresh,
    append,
    pushNotification,
  ];
};

export const usePatchNotification = () => {
  const isApiLoading = useApiLoading(c.PATCH_NOTIFICATION, 'patch');

  const action = React.useCallback((params, cb) => {
    actions.patchNotification(params, cb);

    req.set(c.GET_NOTIFICATION, (state) => ({
      ...(state[c.GET_NOTIFICATION] || {}),
      data: state[c.GET_NOTIFICATION]?.data?.map((item) =>
        item?.id === params?.id ? { ...item, is_new: 0 } : item
      ),
    }));

    req.set(c.GET_NEW_NOTIFICATION, (state) => ({
      ...(state[c.GET_NEW_NOTIFICATION] || {}),
      total_new:
        state[c.GET_NEW_NOTIFICATION]?.total_new > 0
          ? state[c.GET_NEW_NOTIFICATION]?.total_new - 1
          : 0,
    }));
  }, []);

  return [isApiLoading, action];
};

export const useGetNewNotification = (params, cb) => {
  const isApiLoading = useApiLoading(c.GET_NEW_NOTIFICATION, 'get');
  const data = useApiGet(`${c.GET_NEW_NOTIFICATION}.data`, null);
  const pagination = useApiGet(`${c.GET_NEW_NOTIFICATION}.pagination`, {});
  const totalNew = useApiGet(`${c.GET_NEW_NOTIFICATION}.total_new`, {});

  const includes = {
    totalNew,
  };

  const refresh = React.useCallback(
    ($params, $cb) => {
      let args = {
        ...params,
        ...$params,
      };

      if (typeof $params === 'function') args = $params(params);

      actions.getNewNotification(args, $cb);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(params)]
  );

  React.useEffect(() => {
    refresh(params, cb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params), refresh]);

  return [isApiLoading, data, pagination, refresh, includes];
};

export const usePostMarkAllAsRead = () => {
  const isApiLoading = useApiLoading(c.POST_MARK_ALL_AS_READ, 'post');

  const action = React.useCallback((params, cb) => {
    actions.postMarkAllAsRead(params, () => {
      req.set(c.GET_NOTIFICATION, (state) => ({
        ...(state[c.GET_NOTIFICATION] || {}),
        data: [],
      }));

      req.set(c.GET_NEW_NOTIFICATION, (state) => ({
        ...(state[c.GET_NEW_NOTIFICATION] || {}),
        total_new: 0,
      }));

      if (cb) cb?.();
    });
  }, []);

  return [isApiLoading, action];
};
